import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Minus } from 'lucide-react';
import SEO from '../components/SEO';

export default function ProductOverview() {
  const productTypes = [
    {
      id: 'blomsterkort',
      title: 'Blomsterkort',
      description: 'Udforsk mine håndtegnede blomsterkort a6, trykt på en pæn mat og miljøvenlig kvalitet. Skabt med kærlighed til detaljen og det gode håndværk. Perfekt til enhver lejlighed, hvor en personlig hilsen gør en forskel.',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/3b41cd86-39ed-45e0-b435-9533dfc4db81.png',
      alt: 'Håndtegnede blomsterkort og lykønskningskort fra Cards by Eve',
      link: '/produkter/blomsterkort',
      cta: 'Se blomsterkort'
    },
    {
      id: 'printdesign',
      title: 'Printdesign',
      description: 'Specialdesignede printløsninger, logoer, visitkort og grafisk opsætning. Jeg hjælper med at skabe en visuel rød tråd til din virksomhed eller festlige begivenheder som bryllup og barnedåb.',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/837dd0c5-9d80-4673-9933-6b9a45f609af.jpg',
      alt: 'Specialdesignet printdesign og grafiske løsninger fra Cards by Eve',
      link: '/produkter/printdesign',
      cta: 'Se printdesign'
    },
    {
      id: 'reoler',
      title: 'Forskellige reoler',
      description: 'Håndlavede udstillingsreoler i smukt resttræ. Perfekt designet til at præsentere og fremvise kort på en elegant og indbydende måde i butikken eller hjemmet.',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/3097ed61-1367-4683-a2b2-c84a1bc49131.png',
      alt: 'Håndlavede kortreoler i resttræ til udstilling af kort',
      link: '/produkter/forskellige-reoler',
      cta: 'Se reoler'
    },
    {
      id: 'chokolade',
      title: 'Aalborg Chokolade',
      description: 'Eksklusiv mørk chokolade med nødder og unikt print. En perfekt firmagave eller luksuriøs specialitet til dine kunder og forretningsforbindelser.',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/image_2.png',
      alt: 'Aalborg Chokolade med unikt kakaoprint fra Cards by Eve',
      link: '/kollektion/chokolade',
      cta: 'Se chokolade'
    }
  ];

  const overviewSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Produkter & Kollektioner - Cards by Eve",
    "description": "Udforsk hele sortimentet fra Cards by Eve. Vi tilbyder håndtegnede blomsterkort, specialdesignet printdesign, håndlavede kortreoler og luksuschokolade.",
    "hasPart": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Blomsterkort",
        "url": `${window.location.origin}/produkter/blomsterkort`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Printdesign",
        "url": `${window.location.origin}/produkter/printdesign`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Forskellige reoler",
        "url": `${window.location.origin}/produkter/forskellige-reoler`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Aalborg Chokolade",
        "url": `${window.location.origin}/kollektion/chokolade`
      }
    ]
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        title="Produkter & Kollektioner"
        description="Gå på opdagelse i det fulde sortiment fra Cards by Eve. Vi tilbyder håndtegnede blomsterkort, specialdesignet printdesign, håndlavede kortreoler og luksuschokolade."
        schema={overviewSchema}
      />
      {/* Header Section */}
      <section className="py-20 md:py-28 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center space-x-4 mb-6">
              <Minus className="w-6 h-6 text-brand-950" />
              <span className="text-brand-950 text-xs uppercase tracking-[0.4em] font-medium">
                Kollektioner & Design
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl text-brand-950 font-serif leading-tight mb-6">
              Produkttyper
            </h1>
            <p className="text-brand-800 text-lg font-light leading-relaxed max-w-xl">
              Gå på opdagelse i vores nøje udvalgte sortiment af håndtegnede blomsterkort, skræddersyet printdesign, håndlavede reoler og eksklusive specialiteter.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Grid Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {productTypes.map((type, index) => (
              <motion.div
                key={type.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image Container */}
                <div className={`lg:col-span-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-md bg-brand-50">
                    <img
                      src={type.image}
                      alt={type.alt}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>

                {/* Content Container */}
                <div className={`lg:col-span-6 space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="text-3xl md:text-4xl font-serif text-brand-950">
                    {type.title}
                  </h2>
                  <p className="text-brand-600 text-base font-light leading-relaxed">
                    {type.description}
                  </p>
                  <div className="pt-4">
                    <Link
                      to={type.link}
                      className="inline-flex items-center text-brand-950 text-xs uppercase tracking-widest font-bold border-b border-brand-950 pb-2 hover:text-brand-500 hover:border-brand-500 transition-all"
                    >
                      {type.cta}
                      <ArrowRight className="ml-2 w-4 h-4 inline" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
